import MainView from "./MainView.jsx";
import Login from "./Login.jsx";
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { msalInstance, getGraphProfile, logoutUser, isTransientAuthError } from '../auth';
import "./AuthenticatedApp.css";
import { useEffect, useState, useRef, useCallback } from 'react';
import Header from "./Header.jsx";


const AuthenticatedApp = ({ onLogout, onLogin, user, useremail }) => {
  const { pathname } = useLocation();
  const isInvite = pathname.startsWith('/invite');

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Bumping this re-runs the profile load. Without it a token renewal that
  // merely timed out left the app stranded on an error screen, with no way back
  // short of reloading the tab - even though the very next attempt would work.
  const [attempt, setAttempt] = useState(0);
  const [retryable, setRetryable] = useState(false);
  const [err, setErr] = useState("");

  
/**
 * Logs the user out after a period of genuine inactivity.
 *
 * The previous version built its `events` list and `handler` but never called
 * addEventListener, so nothing ever reset the timer and there was no cleanup.
 * Worse, the effect depended on an inline arrow that was recreated every render,
 * so the timer restarted constantly - the behaviour was unpredictable in both
 * directions. That matters more now: being logged out mid-review loses the
 * reviewer's in-progress edits.
 */
const useIdleLogout = (onTimeout, timeoutMs = 60 * 60 * 1000) => {
    const timerRef = useRef(null);
    const callbackRef = useRef(onTimeout);

    // Keep the latest callback without making it an effect dependency.
    useEffect(() => {
        callbackRef.current = onTimeout;
    }, [onTimeout]);

    useEffect(() => {
        const reset = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => callbackRef.current?.(), timeoutMs);
        };

        const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
        const handler = () => {
            if (document.visibilityState === "hidden") return;
            reset();
        };

        events.forEach((event) => window.addEventListener(event, handler, { passive: true }));
        document.addEventListener("visibilitychange", handler);
        reset();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            events.forEach((event) => window.removeEventListener(event, handler));
            document.removeEventListener("visibilitychange", handler);
        };
    }, [timeoutMs]);
}

  const handleIdleLogout = useCallback(async () => {
    try {
      await logoutUser();
    } catch {
      onLogout();
    }
  }, [onLogout]);

  useIdleLogout(handleIdleLogout, 60 * 60 * 1000);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const p = await getGraphProfile();
        if (dead) return;
        setProfile(p);
        setErr("");
        setRetryable(false);

        const account = msalInstance.getAllAccounts()[0] || null;
        console.log("[MSAL] account:", account);
        console.log("[MSAL] idTokenClaims:", account?.idTokenClaims || {});
        console.log("[Graph] /me profile:", p);
      } catch (e) {
        if (dead) return;
        setErr(e?.message || String(e));
        setRetryable(isTransientAuthError(e));
      } finally {
        if (!dead) setLoading(false);
      }
    })();
    return () => {
      dead = true;
    };
  }, [attempt]);

  const retryProfile = useCallback(() => {
    setErr("");
    setRetryable(false);
    setLoading(true);
    setAttempt((n) => n + 1);
  }, []);

  const acct = msalInstance.getAllAccounts()[0] || null;
  const claims = acct?.idTokenClaims || {};
  const departmentRaw = (profile?.department ?? claims?.department ?? "").trim().toUpperCase();
  const officeRaw = (profile?.officeLocation ?? claims?.officeLocation ?? "").toUpperCase().trim();

  const isCorporate = departmentRaw.toUpperCase() === "CORPORATE";

  if (!user) {
    return <Login onLogin={onLogin} />;
  }
  if (!user.username.endsWith("@sidneylee.com")) {
    onLogout();
    return <p>Sign-in restricted to @sidneylee.com accounts only.</p>;
  }
  if (loading) return <p>Loading profile...</p>;
  if (err) {
    return (
      <div className="mx-auto mt-24 max-w-md rounded-lg border border-slate-200 p-6 text-center dark:border-slate-800">
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Could not load your profile
        </p>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          {retryable
            ? "Signing you in took longer than expected. This usually works on a second try."
            : err}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button"
            onClick={retryProfile}
            className="rounded bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={handleIdleLogout}
            className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900"
          >
            Sign out
          </button>
        </div>
        {retryable && (
          <p className="mt-3 break-words text-[11px] text-slate-400 dark:text-slate-600">{err}</p>
        )}
      </div>
    );
  }

  console.log(JSON.stringify(user.name, null, 2));

  return (

    <div className="app-container">
      <Header onLogout={onLogout} user={user.name}/>
      <Routes>
        <Route index element={<RequireCorporate allow={isCorporate}> <MainView branchName={officeRaw} dept={departmentRaw} user={user.name} useremail={useremail}/> </RequireCorporate>} />
      </Routes>
    </div>
  );
};


const AccessDenied = () => {
  const navigate = useNavigate();

  return (
    <div className="access-denied">
      <h1>🚫 Access Denied</h1>
      <p>You do not have permission to view this page.</p>
      <button
        className="back-button"
        onClick={() => navigate("/")}
      >
        ⬅ Back to Dashboard
      </button>
    </div>
  )
}

const RequireCorporate = ({ allow, children }) => {
  if (!allow) return <AccessDenied />;
  return children;
};


export default AuthenticatedApp;